import moment from 'moment';
import type {
  CalendarEventType,
  CalendarComponentEvent,
  CalendarPermissions,
} from '@axiom/validation';

import {
  EDIT_MODE_LEAD_TIME_MINUTES,
  EVENT_STATES,
} from './calendar-events.const';

export type CleanEvent = Omit<CalendarEventType, 'state'>;
type CalendarOnChangeType = {
  newEvents: CalendarEventType[];
  modifiedEvents: CalendarEventType[];
  deleteIds: string[];
  staticEvents: CalendarEventType[];
};

export type CreateNewSlotEventParams = {
  start: Date;
  end: Date;
  action: 'select' | 'click' | 'doubleClick';
  perms: CalendarPermissions;
};

export type CanCreateStaticEventParams = {
  newEvent: CalendarComponentEvent;
  existingEvents: CalendarComponentEvent[];
  perms: CalendarPermissions;
};

export type ValidateAndFilterEventsParams = {
  events: CalendarComponentEvent[];
  backgroundEvents: CalendarComponentEvent[];
  perms: CalendarPermissions;
  bgEventIdsSet: Set<CalendarComponentEvent['id']>;
  mergeEvents: (events: CalendarComponentEvent[]) => CalendarComponentEvent[];
  editableEventId?: string | null;
};

export type CleanBackgroundEventsForStateParams = {
  events: CalendarEventType[];
  permissions: CalendarPermissions;
  bgEventIdsSet: Set<CalendarEventType['id']>;
  timezone?: string;
};

export type GetDeletedEventIdsParams = {
  permissions: CalendarPermissions;
  events: CalendarEventType[];
  formEvents?: CalendarEventType[];
};

export type CleanAndOrganiseEventsParams = {
  permissions: CalendarPermissions;
  initData: CalendarEventType[];
  changedEvents?: CalendarEventType[];
};

export const backgroundNamePrefix = 'BACKGROUND_';
const EVENT_ID_PREFIX = 'UNSAVED';
// Interviews have a minimum of 15 mins in envoy; AFC hard codes to 30 mins
const MIN_BUSY_DURATION_MS = 15 * 60 * 1000;
// Availability blocks must accommodate at least one client-booked
// interview (AFC hard-codes 30 min). Anything shorter is unbookable by clients.
const MIN_FREE_TIME_DURATION_MS = 30 * 60 * 1000;
// Single-click on a static-event-allowed slot is fixed-duration, not a floor.
const CLICK_DURATION_MS = 30 * 60 * 1000;

export const CalendarEventsUtil = {
  /**
   * Selects which events to use for validation purposes
   * Background events take priority if they exist, otherwise use foreground events
   */
  selectEventsForValidation: (
    backgroundEvents: CalendarComponentEvent[],
    foregroundEvents: CalendarComponentEvent[]
  ): CalendarComponentEvent[] => {
    return backgroundEvents.length > 0 ? backgroundEvents : foregroundEvents;
  },

  /**
   * Generates a unique ID for an unsaved calendar event
   */
  generateEventId: (start: Date, end: Date): string => {
    return `${EVENT_ID_PREFIX}_${start.getTime()}_${end.getTime()}`;
  },

  /**
   * Clamps an event's end so its duration meets the busy/free-time floor.
   */
  applyDurationFloor: (busy: boolean, start: Date, end: Date): Date => {
    const min = busy ? MIN_BUSY_DURATION_MS : MIN_FREE_TIME_DURATION_MS;
    return end.getTime() - start.getTime() < min
      ? new Date(start.getTime() + min)
      : end;
  },

  /**
   * Creates a new calendar event from a selected time slot
   */
  createNewSlotEvent: ({
    start,
    end,
    action,
    perms,
  }: CreateNewSlotEventParams): CalendarComponentEvent => {
    const canCreateStaticEvent =
      perms.staticEvent.canCreate || perms.staticEvent.canDragToCreate;
    const adjustedEnd =
      canCreateStaticEvent && action === 'click'
        ? new Date(start.getTime() + CLICK_DURATION_MS)
        : CalendarEventsUtil.applyDurationFloor(
            canCreateStaticEvent,
            start,
            end
          );

    return {
      id: CalendarEventsUtil.generateEventId(start, adjustedEnd),
      title: canCreateStaticEvent ? 'Interview' : '',
      start,
      end: adjustedEnd,
      busy: canCreateStaticEvent,
      error: null,
      state: EVENT_STATES.NEW,
    };
  },

  /**
   * Carves the busy events out of a free-time event's interval, returning the
   * non-overlapping free-time pieces that remain. Mirrors the backend
   * `CalendarsService.splitCalendarEvents` algorithm so the UI shows the same
   * result the server will persist. Used when a new/modified free-time block
   * overhangs an existing interview — the user sees the trimmed result
   * immediately instead of a visually overlapping block.
   *
   * Returns an empty array when the busy events fully cover the free-time
   * interval. Pieces inherit the original event's metadata; each piece gets
   * a fresh id derived from its own start/end so React keys stay unique.
   */
  splitFreeTimeAroundBusy: (
    freeTimeEvent: CalendarComponentEvent,
    busyEvents: readonly CalendarComponentEvent[]
  ): CalendarComponentEvent[] => {
    type Frame = { start: Date; end: Date };
    let frames: Frame[] = [
      { start: freeTimeEvent.start, end: freeTimeEvent.end },
    ];

    busyEvents.forEach(busy => {
      const idx = frames.findIndex(
        f =>
          f.start.getTime() < busy.end.getTime() &&
          f.end.getTime() > busy.start.getTime()
      );
      const frame = frames[idx];
      if (!frame) return;
      const replacement: Frame[] = [];
      if (busy.start.getTime() > frame.start.getTime()) {
        replacement.push({ start: frame.start, end: busy.start });
      }
      if (busy.end.getTime() < frame.end.getTime()) {
        replacement.push({ start: busy.end, end: frame.end });
      }
      frames = [
        ...frames.slice(0, idx),
        ...replacement,
        ...frames.slice(idx + 1),
      ];
    });

    return frames
      .filter(f => f.start.getTime() !== f.end.getTime())
      .map(f => ({
        ...freeTimeEvent,
        id: CalendarEventsUtil.generateEventId(f.start, f.end),
        start: f.start,
        end: f.end,
        state: EVENT_STATES.NEW,
      }));
  },

  /**
   * Checks if a new event is within the boundaries of existing events
   */
  isEventWithinBoundaries: (
    newEvent: CalendarComponentEvent,
    existingEvents: CalendarComponentEvent[]
  ): boolean => {
    return existingEvents.some(event => {
      return (
        moment(newEvent.start).isBetween(
          event.start,
          event.end,
          undefined,
          '[]'
        ) &&
        moment(newEvent.end).isBetween(event.start, event.end, undefined, '[]')
      );
    });
  },

  /**
   * Checks if a slot event can be created based on permissions and action type
   */
  canCreateSlotEvent: (
    perms: CalendarPermissions,
    action: 'select' | 'click' | 'doubleClick'
  ): boolean => {
    if (
      perms.staticEvent.canCreate &&
      !perms.staticEvent.canDragToCreate &&
      (action === 'select' || action === 'doubleClick')
    ) {
      return false;
    }
    return true;
  },

  /**
   * Validates if a static event can be created based on permissions and existing events
   */
  canCreateStaticEvent: ({
    newEvent,
    existingEvents,
    perms,
  }: CanCreateStaticEventParams): boolean => {
    const isWithinBoundary = CalendarEventsUtil.isEventWithinBoundaries(
      newEvent,
      existingEvents
    );

    if (!perms.staticEvent.canCreateFreely && !isWithinBoundary) {
      return false;
    }

    return true;
  },

  /**
   * Finds the first unsaved static event in the events array
   */
  findUnsavedStaticEvent: (
    events: CalendarComponentEvent[]
  ): CalendarComponentEvent | undefined => {
    return events.find(
      event => event.busy && event.state !== EVENT_STATES.SAVED
    );
  },

  /**
   * Removes unsaved static event if permissions don't allow multiple and one exists
   */
  removeUnsavedStaticEventIfNeeded: (
    events: CalendarComponentEvent[],
    perms: CalendarPermissions
  ): CalendarComponentEvent[] => {
    const unsavedStaticEvent =
      CalendarEventsUtil.findUnsavedStaticEvent(events);

    return !perms.staticEvent.canCreateMultiple && unsavedStaticEvent
      ? events.filter(e => e.id !== unsavedStaticEvent.id)
      : [...events];
  },

  /**
   * Validates events and filters out background events
   */
  validateAndFilterEvents: ({
    events,
    backgroundEvents,
    perms,
    bgEventIdsSet,
    mergeEvents,
    editableEventId,
  }: ValidateAndFilterEventsParams): CalendarComponentEvent[] => {
    const merged = mergeEvents(events);
    const validated = CalendarEventsUtil.setEventError(
      {
        foreground: merged,
        background: backgroundEvents,
      },
      perms,
      editableEventId
    );

    return validated.filter(event => !bgEventIdsSet.has(event.id));
  },
  cleanForegroundEventsForState: (
    items: CalendarEventType[],
    permissions: CalendarPermissions,
    timezone?: string,
    editableEventId?: string | null
  ): CalendarComponentEvent[] => {
    const blockingGranularity =
      CalendarEventsUtil.dateCheckGranularity(permissions);
    const now = timezone ? moment.tz(timezone) : moment();

    const stateSet = new Set([EVENT_STATES.NEW, EVENT_STATES.MODIFIED]);
    return CalendarEventsUtil.translateFromStartTimeEndTimeProps(items)
      .filter(event => {
        if (event.id === editableEventId) return true;
        if (stateSet.has(event.state)) return true;
        // Busy events use strict-instant past-end so today's live
        // interviews remain visible/editable. Free-time events use the
        // same day-bucket past-end that setEventError uses to flag them
        // Unavailable — otherwise today's saved availability would render
        // initially and then flip Unavailable the moment any edit ran.
        if (event.busy) {
          return !CalendarEventsUtil.isPastEnded(event.end, timezone);
        }
        const eventEnd = timezone
          ? moment(event.end).tz(timezone)
          : moment(event.end);
        return eventEnd.isAfter(now, blockingGranularity);
      })
      .map(e => {
        // Nudge a stale (past-start) availability block forward to the next
        // hour so it renders as a valid, saveable slot instead of an
        // already-unavailable one. This applies ONLY to free-time blocks
        // (busy === false).
        if (!e.busy) {
          const eventStart = timezone
            ? moment(e.start).tz(timezone)
            : moment(e.start);
          if (
            e.state === EVENT_STATES.SAVED &&
            eventStart.isBefore(now, blockingGranularity)
          ) {
            e.start = now
              .clone()
              .set({ minutes: 0, seconds: 0, milliseconds: 0 })
              .add(1, 'hour')
              .toDate();
            e.state = EVENT_STATES.MODIFIED;
          }
        }

        e.name = CalendarEventsUtil.setForegroundName(e.name);

        return e;
      });
  },
  cleanBackgroundEventsForState: ({
    events,
    permissions,
    bgEventIdsSet,
    timezone,
  }: CleanBackgroundEventsForStateParams) => {
    bgEventIdsSet.clear();
    const blockingGranularity =
      CalendarEventsUtil.dateCheckGranularity(permissions);
    const now = timezone ? moment.tz(timezone) : moment();

    const stateSet = new Set([EVENT_STATES.NEW, EVENT_STATES.MODIFIED]);
    return CalendarEventsUtil.translateFromStartTimeEndTimeProps(
      events
        .filter(event => {
          const eventEnd = timezone
            ? moment(event.endTime).tz(timezone)
            : moment(event.endTime);
          return (
            stateSet.has(event.state) ||
            eventEnd.isAfter(now, blockingGranularity)
          );
        })
        .map((bgE: CalendarEventType) => {
          bgEventIdsSet.add(bgE.id);

          return {
            ...bgE,
            name: CalendarEventsUtil.setBackgroundName(bgE.name),
          };
        })
    );
  },
  canAddEvent: (permissions: CalendarPermissions, startDate: string) => {
    return (
      (permissions.eventBoundary.canCreate ||
        permissions.eventBoundary.canDragToCreate ||
        permissions.staticEvent.canCreate ||
        permissions.staticEvent.canDragToCreate) &&
      moment(startDate).isAfter(
        moment(),
        CalendarEventsUtil.dateCheckGranularity(permissions)
      )
    );
  },
  dateCheckGranularity: (permissions: CalendarPermissions) => {
    return permissions.allEvents.canSameDay ? 'hour' : 'day';
  },
  isInThePast: (
    date: CalendarEventType['endTime'] | Date,
    permissions: CalendarPermissions,
    timezone?: string
  ) => {
    const now = timezone ? moment.tz(timezone) : moment();
    return moment(date).isSameOrBefore(
      now,
      CalendarEventsUtil.dateCheckGranularity(permissions)
    );
  },
  // Strict-instant past-end check. Use this — not `isInThePast` — when
  // deciding whether to hide / treat as historical an existing event.
  // `isInThePast` uses hour-bucket equality, which encodes the lead-time
  // gate for *new* bookings; for filtering existing events, bucket
  // equality wrongly drops events ending later in the current clock-hour
  // (they're still ongoing and still potential conflicts).
  isPastEnded: (
    endTime: CalendarEventType['endTime'] | Date,
    timezone?: string
  ) => {
    const now = timezone ? moment.tz(timezone) : moment();
    return moment(endTime).isBefore(now);
  },
  translateFromStartTimeEndTimeProps: (
    events: CalendarEventType[]
  ): CalendarComponentEvent[] => {
    return events.reduce(
      (
        data: CalendarComponentEvent[],
        event: CalendarEventType
      ): CalendarComponentEvent[] => {
        const { endTime, startTime, state, ...rest } = event;
        const item = {
          ...rest,
          state: state ?? EVENT_STATES.SAVED,
          end: moment(endTime).toDate(),
          start: moment(startTime).toDate(),
        };

        data.push(item);
        return data;
      },
      []
    );
  },
  translateToStartTimeEndTimeProps: (
    events: CalendarComponentEvent[]
  ): CalendarEventType[] => {
    return events.reduce(
      (
        data: CalendarEventType[],
        event: CalendarComponentEvent
      ): CalendarEventType[] => {
        const { end, start, ...rest } = event;
        const item: CalendarEventType = {
          ...rest,
          endTime: moment(end).toISOString(),
          startTime: moment(start).toISOString(),
        };

        data.push(item);
        return data;
      },
      []
    );
  },
  setForegroundName: (eventName?: string | null) => {
    if (!eventName) return `EVENT`;

    return eventName.includes(backgroundNamePrefix)
      ? eventName.slice(backgroundNamePrefix.length)
      : eventName;
  },
  setBackgroundName: (eventName?: string | null) => {
    if (!eventName) return `${backgroundNamePrefix}EVENT`;

    return eventName.includes(backgroundNamePrefix)
      ? eventName
      : `${backgroundNamePrefix}${eventName}`;
  },
  sortEvents: (events: CalendarComponentEvent[]) => {
    if (events.length >= 2) {
      events.sort((a, b) => {
        const aStart = moment(a.start);
        const bStart = moment(b.start);
        if (aStart.isBefore(bStart)) return -1;
        if (aStart.isAfter(bStart)) return 1;
        const aEnd = moment(a.end);
        const bEnd = moment(b.end);
        if (aEnd.isBefore(bEnd)) return -1;
        return aEnd.isAfter(bEnd) ? 1 : 0;
      });
    }
    return events;
  },
  getDeletedEventIds: ({
    permissions,
    events,
    formEvents,
  }: GetDeletedEventIdsParams) => {
    if (!formEvents) {
      return [];
    }

    const formIds = new Set(formEvents.map(e => e.id));

    return events
      .filter(event => {
        return !CalendarEventsUtil.isInThePast(event.endTime, permissions);
      })
      .map(e => e.id)
      .reduce<CalendarOnChangeType['deleteIds']>((crnt, id) => {
        if (!formIds.has(id) && id) {
          crnt.push(id);
        }
        return crnt;
      }, []);
  },
  cleanAndOrganiseEvents: ({
    permissions,
    initData,
    changedEvents,
  }: CleanAndOrganiseEventsParams) => {
    const baseDataset: CalendarOnChangeType = {
      newEvents: [],
      modifiedEvents: [],
      deleteIds: [],
      staticEvents: [],
    };

    if (!changedEvents) {
      return baseDataset;
    }

    const cleanEvents =
      changedEvents.length > 0
        ? changedEvents.reduce<CalendarOnChangeType>(
            (data, event) => {
              const tmpEvent = {
                ...event,
                id: event.state === EVENT_STATES.NEW ? null : event.id,
              };

              const cleanEvent = (toCleanEvent: CalendarEventType) => {
                return Object.entries({
                  ...toCleanEvent,
                  state: null,
                }).reduce((crnt, [key, value]) => {
                  if (value !== null) Object.assign(crnt, { [key]: value });

                  return crnt;
                }, {} as CleanEvent);
              };

              if (tmpEvent.state === EVENT_STATES.NEW) {
                data[tmpEvent.busy ? 'staticEvents' : 'newEvents'].push(
                  cleanEvent(tmpEvent)
                );
              } else if (tmpEvent.state === EVENT_STATES.MODIFIED) {
                data.modifiedEvents.push(cleanEvent(tmpEvent));
              }

              return data;
            },
            { ...baseDataset }
          )
        : baseDataset;

    cleanEvents.deleteIds = CalendarEventsUtil.getDeletedEventIds({
      permissions,
      events: initData,
      formEvents: changedEvents,
    });

    return cleanEvents;
  },
  setEventError: (
    {
      foreground,
      background,
    }: {
      foreground: CalendarComponentEvent[];
      background?: CalendarComponentEvent[];
    },
    permissions: CalendarPermissions,
    editableEventId?: string | null
  ) => {
    const { fgFreetime, fgInterviews } = foreground.reduce<{
      fgFreetime: CalendarComponentEvent[];
      fgInterviews: CalendarComponentEvent[];
    }>(
      (crnt, event) => {
        if (event.busy) {
          crnt.fgInterviews.push(event);
        } else {
          crnt.fgFreetime.push(event);
        }
        return crnt;
      },
      { fgFreetime: [], fgInterviews: [] }
    );
    const { bgFreetime, bgInterviews } = (background ?? []).reduce<{
      bgFreetime: CalendarComponentEvent[];
      bgInterviews: CalendarComponentEvent[];
    }>(
      (crnt, event) => {
        if (event.busy) {
          crnt.bgInterviews.push(event);
        } else {
          crnt.bgFreetime.push(event);
        }
        return crnt;
      },
      { bgFreetime: [], bgInterviews: [] }
    );

    const stateSet = new Set([EVENT_STATES.NEW, EVENT_STATES.MODIFIED]);
    const checkStaticEvent = (event: CalendarComponentEvent) => {
      return (
        stateSet.has(event.state) &&
        ![...fgFreetime, ...bgFreetime].some(fte => {
          // is staticEvent in valid time slot
          return (
            moment(event.start).isBetween(
              fte.start,
              fte.end,
              undefined,
              '[]'
            ) &&
            moment(event.end).isBetween(fte.start, fte.end, undefined, '[]')
          );
        })
      );
    };

    // A new/modified busy event that overlaps any other busy event (fg or
    // bg) is invalid — flag it so the form can't submit. Excludes self via
    // id match. Saved-vs-saved overlaps in pre-existing data are not
    // retroactively flagged because we only check NEW/MODIFIED state here.
    const overlapsOtherBusy = (event: CalendarComponentEvent) =>
      [...fgInterviews, ...bgInterviews].some(
        other =>
          other.id !== event.id &&
          moment(event.start).isBefore(other.end) &&
          moment(event.end).isAfter(other.start)
      );

    fgInterviews.forEach((event: CalendarComponentEvent) => {
      const isEdited = stateSet.has(event.state);
      if (event.id === editableEventId) {
        // Editable event:
        // - Skip the past-end check (a live interview legitimately has its
        //   end in the current hour-bucket; we don't want to flag it on
        //   open).
        // - Once dragged (state NEW/MODIFIED), enforce a 5-minute minimum
        //   lead time on the new start. Uses an instant comparison rather
        //   than `isInThePast`'s hour-bucket logic — bucket gating is for
        //   new-booking lead time, not for re-positioning an existing
        //   event. 5 min matches the calendar's 5-min slot grid.
        // - Overlap and availability (checkStaticEvent) rules still apply
        //   so the user can't reschedule onto another busy event or
        //   outside the talent's availability.
        if (
          (isEdited &&
            moment(event.start).isBefore(
              moment().add(EDIT_MODE_LEAD_TIME_MINUTES, 'minutes')
            )) ||
          (!permissions.staticEvent.canCreateFreely &&
            checkStaticEvent(event)) ||
          (isEdited && overlapsOtherBusy(event))
        ) {
          event.error = 'Unavailable';
        }
        return;
      }
      if (
        (!permissions.staticEvent.canCreateFreely && checkStaticEvent(event)) ||
        CalendarEventsUtil.isPastEnded(event.end) ||
        (isEdited &&
          CalendarEventsUtil.isInThePast(event.start, permissions)) ||
        (isEdited && overlapsOtherBusy(event))
      ) {
        event.error = 'Unavailable';
      }
    });

    fgFreetime.forEach((event: CalendarComponentEvent) => {
      if (
        moment(event.end).isSameOrBefore(
          moment(),
          CalendarEventsUtil.dateCheckGranularity(permissions)
        ) ||
        moment(event.start).isSameOrBefore(
          moment(),
          CalendarEventsUtil.dateCheckGranularity(permissions)
        )
      ) {
        event.error = 'Unavailable';
      }
    });

    return [...fgFreetime, ...fgInterviews];
  },
  hasAvailableFreeTimeSlots(
    events: CalendarEventType[] | null | undefined,
    permissions: CalendarPermissions
  ): boolean {
    return (
      !!events &&
      events.some(
        event =>
          !event.busy &&
          !CalendarEventsUtil.isInThePast(event.startTime, permissions)
      )
    );
  },
};
